import { memo, useCallback, useRef, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { PortalHeader } from './PortalHeader';
import { PortalSidebar } from './PortalSidebar';

const LAYOUT = {
  sidebarExpandedWidth: 260,
  sidebarCollapsedWidth: 80,
} as const;

/* ── Mobile layout: header + slide-in sidebar ── */
const MobileLayout = memo(function MobileLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleOpen = useCallback(() => setSidebarOpen(true), []);
  const handleClose = useCallback(() => setSidebarOpen(false), []);

  return (
    <div className="bg-muted font-inter flex min-h-screen flex-col">
      {/* Header */}
      <PortalHeader className="sticky top-0 z-30" onLogoClick={handleOpen} />

      {/* Report content */}
      <main className="bg-background flex-1 overflow-hidden">
        <Outlet />
      </main>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-60 bg-black/40 transition-opacity"
          onClick={handleClose}
        />
      )}

      {/* Slide-in sidebar */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-70 h-screen transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
        style={{ width: LAYOUT.sidebarExpandedWidth }}
      >
        <PortalSidebar
          isExpanded={true}
          onToggle={handleClose}
          onSelect={handleClose}
        />
      </aside>
    </div>
  );
});

export const PortalLayout = memo(function PortalLayout() {
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const isMobile = useRef(window.innerWidth < 768).current;

  const handleSidebarToggle = useCallback(() => {
    setIsSidebarExpanded(prev => !prev);
  }, []);

  const currentSidebarWidth = isSidebarExpanded
    ? LAYOUT.sidebarExpandedWidth
    : LAYOUT.sidebarCollapsedWidth;

  const mainStyle = useMemo(
    () => ({
      paddingLeft: isMobile ? 0 : `${currentSidebarWidth}px`,
      minHeight: '100vh',
    }),
    [currentSidebarWidth, isMobile],
  );

  const sidebarStyle = useMemo(
    () => ({
      width: `${currentSidebarWidth}px`,
    }),
    [currentSidebarWidth],
  );

  // Mobile: fullscreen report + bottom nav
  if (isMobile) {
    return <MobileLayout />;
  }

  return (
    <div className="bg-muted font-inter flex min-h-screen flex-col">
      {/* Sidebar - Fixed left */}
      <aside
        className="fixed top-0 left-0 z-40 h-screen transition-all duration-300 ease-in-out"
        style={sidebarStyle}
      >
        <PortalSidebar
          isExpanded={isSidebarExpanded}
          onToggle={handleSidebarToggle}
        />
      </aside>

      {/* Main Content Area */}
      <div
        className="flex flex-1 flex-col transition-all duration-300 ease-in-out"
        style={mainStyle}
      >
        {/* Header */}
        <PortalHeader className="sticky top-0 z-30" />

        {/* Page Content */}
        <main className="bg-background flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
});

export default PortalLayout;
